import { TrashCan } from '@carbon/react/icons';
import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@carbon/react';
import { FC, useMemo } from 'react';

import { DataTable } from 'components/data-table';
import { PlainLink } from 'components/link';

import styles from './entity-selection-table.module.scss';

/** Entity shape accepted by the selection table */
export interface EntitySelectionRow {
  entityId: number;
  name: string;
  href: string;
  address: string | null;
  entityType: string | null;
  role: string;
}

/** Internal DataTable row shape */
type TableRow = Record<string, unknown> & EntitySelectionRow & { id: string };

/** Readonly table config — no sorting, filtering, or footer */
const READONLY_TABLE_CONFIG = {
  sorting: false,
  columnResizing: false,
  columnReordering: false,
  columnPinning: false,
  columnVisibility: false,
  globalSearch: false,
  columnFilters: false,
  hideFooter: true,
} as const;

export interface EntitySelectionTableProps {
  entities: EntitySelectionRow[];
  /** When provided, renders a delete button per row. */
  onRemove?: (entityId: number, role: string) => void;
}

/**
 * Readonly DataTable showing selected entities with role, name link,
 * address, and entity type. Optionally shows a delete column when
 * onRemove is provided.
 * @param {EntitySelectionTableProps} props - Entities and optional onRemove
 * @return {JSX.Element} Entity selection DataTable
 */
const EntitySelectionTable: FC<EntitySelectionTableProps> = ({
  entities,
  onRemove,
}) => {
  const rows: TableRow[] = useMemo(
    () =>
      entities.map((e, i) => ({
        ...e,
        id: `${e.entityId}-${e.role}-${i}`,
        role: e.role === 'sender' ? 'Sender' : 'Receiver',
      })),
    [entities]
  );

  const columns: ColumnDef<TableRow>[] = useMemo(() => {
    const cols: ColumnDef<TableRow>[] = [
      { id: 'role', accessorKey: 'role', header: 'Role', size: 80 },
      {
        id: 'insightsEntity',
        header: 'Insights Entity',
        size: 800,
        meta: { wrap: true },
        cell: ({ row }) => (
          <div className={styles.entityCell}>
            <PlainLink
              href={row.original.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {row.original.name}
            </PlainLink>
            <div className={styles.entityDetails}>
              <span className={styles.label}>Address:</span>{' '}
              {row.original.address || '--'}
            </div>
            <div className={styles.entityDetails}>
              <span className={styles.label}>Entity Type:</span>{' '}
              {row.original.entityType || '--'}
            </div>
          </div>
        ),
      },
    ];

    if (onRemove) {
      cols.push({
        id: 'delete',
        header: '',
        size: 50,
        cell: ({ row }) => (
          <div className={styles.deleteCell}>
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={TrashCan}
              iconDescription="Remove entity"
              className={styles.deleteButton}
              onClick={() =>
                onRemove(row.original.entityId, row.original.role.toLowerCase())
              }
            />
          </div>
        ),
      });
    }

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entities, onRemove]);

  return (
    <DataTable
      data={rows}
      columns={columns}
      getRowId={(row) => row.id}
      config={READONLY_TABLE_CONFIG}
    />
  );
};

export { EntitySelectionTable };
